
import {
  CountryConfigResponse,
  ICountryAdapterData,
  ICountryAdapterPatchData,
  ICountryAdapterPatchRequest,
  ICountry,
  ICountryConfigRequest,
  ICountryConfigResponse,
} from "@/entities";
import { BvTableFieldArray } from "bootstrap-vue";
import IconEdit from "@/components/common/icon/IconEdit.vue";
import { isUrlMinimalFormatValid, normalizeUrlValue } from "@/plugins/vee-validate";
import { IPageResponse } from "@/services";
import { ValidationObserver } from "vee-validate";
import Vue from "vue";
import Component from "vue-class-component";
import Multiselect from "vue-multiselect";
import { Action, Getter } from "vuex-class";

interface ICountryAdapterRow {
  adapterId: number;
  adapterName: string;
  originalUrl: string;
  currentUrl: string;
  isEditing: boolean;
}

@Component({
  name: "countries-page",
  components: {
    IconEdit,
    ValidationObserver,
    Multiselect,
  },
})
export default class CountriesPage extends Vue {
  @Getter("getCountriesPage") countriesPage!: IPageResponse<ICountry>;
  @Getter("getIsAdmin") isAdmin!: boolean;
  @Action("fetchCashCountryConfig") fetchCashCountryConfig!: (
    code: string
  ) => Promise<ICountryConfigResponse | null>;
  countrySelected: ICountry | null = null;
  countryOptions: ICountry[] = [];
  countryConfig: CountryConfigResponse | null = null;
  countryAdapterRows: ICountryAdapterRow[] = [];

  get countryAdapterFields(): BvTableFieldArray {
    return [
      {
        key: "actions-left",
        label: this.$t("column.action") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "adapterName",
        label: this.$t("column.adapter") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "url",
        label: this.$t("column.url") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
    ];
  }

  mounted(): void {
    const countries = this.countriesPage.content;
    if (countries) {
      this.countryOptions = countries;
    }
  }

  async onSelectCountry(country?: ICountry): Promise<void> {
    if (country) {
      const response = await this.$services.country.getCountryConfigByCode(country.code!);
      if (response) {
        this.countryConfig = response;
        await this.loadCountryAdaptersByCountryCode(country.code!);
      } else {
        this.$swal.fire({
          icon: "info",
          title: this.$t("messages.country.config.not.exists") as string,
          showConfirmButton: false,
          timer: 1500,
        });
        this.countryConfig = this.initialCountryConfig();
        this.countryAdapterRows = [];
      }
    }
  }

  async loadCountryAdaptersByCountryCode(countryCode: string): Promise<void> {
    if (!countryCode || !this.isAdmin) {
      this.countryAdapterRows = [];
      return;
    }

    const countryAdapters =
      await this.$services.adapter.getCountryAdaptersByCountryCodeAndNonAssignable(countryCode);
    this.countryAdapterRows = (countryAdapters || []).map((countryAdapter) =>
      this.mapCountryAdapterRow(countryAdapter)
    );
  }

  mapCountryAdapterRow(countryAdapter: ICountryAdapterData): ICountryAdapterRow {
    const adapterUrl = normalizeUrlValue(countryAdapter.url);

    return {
      adapterId: countryAdapter.adapterId,
      adapterName: countryAdapter.adapterName,
      originalUrl: adapterUrl,
      currentUrl: adapterUrl,
      isEditing: false,
    };
  }

  enableCountryAdapterEdition(adapterId: number): void {
    const countryAdapterRow = this.countryAdapterRows.find((row) => row.adapterId === adapterId);
    if (!countryAdapterRow) {
      return;
    }

    countryAdapterRow.isEditing = true;
  }

  private getModifiedAdapterUrl(row: ICountryAdapterRow): string | null {
    const normalizedCurrentUrl = normalizeUrlValue(row.currentUrl);
    const normalizedOriginalUrl = normalizeUrlValue(row.originalUrl);

    if (!normalizedCurrentUrl.length && !normalizedOriginalUrl.length) {
      return null;
    }

    if (normalizedCurrentUrl === normalizedOriginalUrl) {
      return null;
    }

    return normalizedCurrentUrl;
  }

  private buildCountryAdapterPatchDelta(): ICountryAdapterPatchData[] {
    return this.countryAdapterRows
      .map((row) => {
        const modifiedAdapterUrl = this.getModifiedAdapterUrl(row);
        if (modifiedAdapterUrl === null) {
          return null;
        }

        if (
          modifiedAdapterUrl.length &&
          !isUrlMinimalFormatValid(modifiedAdapterUrl, { allowEmpty: false })
        ) {
          return null;
        }

        return {
          adapterId: row.adapterId,
          url: modifiedAdapterUrl,
        };
      })
      .filter((countryAdapter): countryAdapter is ICountryAdapterPatchData => !!countryAdapter);
  }

  private async saveModifiedCountryAdapters(countryCode: string): Promise<boolean> {
    if (!countryCode || !this.isAdmin) {
      return true;
    }

    const countryAdapters = this.buildCountryAdapterPatchDelta();
    if (!countryAdapters.length) {
      return true;
    }

    const patchRequest: ICountryAdapterPatchRequest = {
      countryAdapters,
    };
    const updatedAdapters =
      await this.$services.adapter.patchCountryAdaptersByCountryCodeAndNonAssignable(
        countryCode,
        patchRequest
      );

    return !!updatedAdapters;
  }

  private resetCountryAdapterEditionState(): void {
    this.countryAdapterRows = this.countryAdapterRows.map((row) => ({
      ...row,
      isEditing: false,
    }));
  }

  initialCountryConfig(): CountryConfigResponse {
    return {
      enabledQueryFleetByPlate: false,
      enabledQueryFleetByInternalNumber: false,
      disabledExportEmployees: false,
      enabledOrderFlowApproval: false,
      enabledCancelOrders: false,
      enabledPlanningDate: false,
      adjustmentMinutes: 0,
      enabledAssignServicePointByZone: false,
    };
  }

  async saveCountryConfig(): Promise<void> {
    if (!this.countryConfig) {
      return;
    }

    const countryCode = this.countrySelected?.code || "";
    const request: ICountryConfigRequest = {
      countryCode,
      enabledQueryFleetByPlate: this.countryConfig.enabledQueryFleetByPlate || false,
      enabledQueryFleetByInternalNumber:
        this.countryConfig.enabledQueryFleetByInternalNumber || false,
      disabledExportEmployees: this.countryConfig.disabledExportEmployees || false,
      enabledOrderFlowApproval: this.countryConfig.enabledOrderFlowApproval || false,
      enabledCancelOrders: this.countryConfig.enabledCancelOrders || false,
      enabledPlanningDate: this.countryConfig.enabledPlanningDate || false,
      adjustmentMinutes: this.countryConfig.adjustmentMinutes || 0,
      enabledAssignServicePointByZone: this.countryConfig.enabledAssignServicePointByZone || false,
    };

    const savedConfig = await this.$services.country.saveCountryConfig(request);
    if (!savedConfig) {
      return;
    }

    const savedAdapters = await this.saveModifiedCountryAdapters(countryCode);
    if (!savedAdapters) {
      return;
    }

    this.resetCountryAdapterEditionState();

    await this.fetchCashCountryConfig(countryCode);
    await this.loadCountryAdaptersByCountryCode(countryCode);
    this.makeToast("success", this.$t("messages.config.saved.ok") as string);
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }
}
